import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertTriangle, Phone, MapPin, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * UrgenciaPage
 *
 * Full-screen, blocking screen shown to a patient whose intake form detected
 * a red flag symptom. The intake form is intentionally NOT submitted — the
 * patient must seek presential care.
 *
 * Specs:
 * - Full-screen (not a modal) — patient cannot dismiss
 * - No "continue anyway" button
 * - Two prominent CTAs: Fleni guardia + SAME 107
 * - Discreet secondary: "Volver al formulario y modificar mis síntomas"
 * - Mobile-first, high-contrast, accessible (focus, contrast, large text)
 * - Audit event is logged from the form before redirecting
 */

// Emergency contacts per institution. Easy to edit; will be moved to a
// configurable per-institution model when a second client requires it.
const EMERGENCY_CONTACTS = {
  fleni: {
    guardia_name: "Guardia de Fleni",
    guardia_address: "Montañeses 2325, CABA, Argentina",
    guardia_maps_url: "https://maps.google.com/?q=Monta%C3%B1eses+2325,+CABA,+Argentina",
    emergency_phone_label: "SAME",
    emergency_phone: "107",
  },
  // Fallback used for non-Fleni institutions and direct intakes
  default: {
    guardia_name: "Guardia médica más cercana",
    guardia_address: null,
    guardia_maps_url: "https://maps.google.com/?q=guardia+medica+cerca+de+mi",
    emergency_phone_label: "SAME",
    emergency_phone: "107",
  }
};

const UrgenciaPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const primaryButtonRef = useRef(null);

  const {
    red_flag_label,
    institution_slug,
    message,
  } = location.state || {};

  const contacts = EMERGENCY_CONTACTS[institution_slug] || EMERGENCY_CONTACTS.default;

  // Auto-focus the first CTA so keyboard users land on a meaningful action
  useEffect(() => {
    if (primaryButtonRef.current) {
      primaryButtonRef.current.focus();
    }
  }, []);

  const handleBackToForm = () => {
    // Go back to the previous page (the intake form). The browser will
    // restore form state, but the safety-net useEffect will redirect here
    // again if a red flag is still checked. The patient must uncheck it
    // first.
    navigate(-1);
  };

  return (
    <div
      className="min-h-screen w-full bg-red-700 flex flex-col"
      role="alertdialog"
      aria-labelledby="urgencia-heading"
      aria-describedby="urgencia-description"
      data-testid="urgencia-screen"
    >
      <main className="flex-1 flex items-center justify-center px-4 py-8 sm:py-12">
        <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header band — high contrast, red */}
          <div className="bg-red-700 text-white px-6 py-8 text-center">
            <div
              className="mx-auto w-20 h-20 bg-white/15 rounded-full flex items-center justify-center mb-4"
              aria-hidden="true"
            >
              <AlertTriangle className="h-12 w-12 text-white" strokeWidth={2.5} />
            </div>
            <h1
              id="urgencia-heading"
              className="text-3xl sm:text-4xl font-bold leading-tight"
            >
              Necesitás atención inmediata
            </h1>
            <p className="mt-3 text-white/95 text-base sm:text-lg leading-relaxed">
              Los síntomas que indicaste no pueden resolverse por una consulta online.
              Buscá atención presencial ahora.
            </p>
          </div>

          {/* Body */}
          <div className="px-6 py-6 sm:px-8 sm:py-8 space-y-5">
            {/* Detected symptom (if available) */}
            {red_flag_label && (
              <div
                id="urgencia-description"
                className="bg-red-50 border-2 border-red-200 rounded-lg px-4 py-3"
                data-testid="urgencia-detected-symptom"
              >
                <p className="text-sm font-medium text-red-900">
                  Síntoma reportado: <strong>{red_flag_label}</strong>
                </p>
              </div>
            )}

            {/* Optional custom message override */}
            {message && (
              <p className="text-base text-slate-800 leading-relaxed">{message}</p>
            )}

            {/* CTAs */}
            <div className="space-y-3 pt-2">
              {/* CTA 1: Guardia */}
              <a
                ref={primaryButtonRef}
                href={contacts.guardia_maps_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="urgencia-guardia-btn"
                className="block w-full bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-4 focus:ring-red-300 active:bg-red-800 text-white rounded-xl px-5 py-5 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 mt-0.5">
                    <MapPin className="h-7 w-7" strokeWidth={2.5} />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="text-lg sm:text-xl font-bold">
                      {contacts.guardia_name}
                    </div>
                    {contacts.guardia_address && (
                      <div className="text-sm sm:text-base text-white/95 mt-0.5">
                        {contacts.guardia_address}
                      </div>
                    )}
                    <div className="text-xs text-white/85 mt-1">
                      Tocá para abrir la ubicación en el mapa
                    </div>
                  </div>
                </div>
              </a>

              {/* CTA 2: SAME 107 */}
              <a
                href={`tel:${contacts.emergency_phone}`}
                data-testid="urgencia-same-btn"
                className="block w-full bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-4 focus:ring-orange-300 active:bg-orange-800 text-white rounded-xl px-5 py-5 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 mt-0.5">
                    <Phone className="h-7 w-7" strokeWidth={2.5} />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="text-lg sm:text-xl font-bold">
                      Llamar al {contacts.emergency_phone_label}
                    </div>
                    <div className="text-2xl sm:text-3xl font-extrabold tracking-wide mt-0.5">
                      {contacts.emergency_phone}
                    </div>
                    <div className="text-xs text-white/85 mt-1">
                      Servicio de emergencias médicas
                    </div>
                  </div>
                </div>
              </a>
            </div>

            {/* Secondary — discreet */}
            <div className="pt-4 border-t border-slate-200">
              <Button
                onClick={handleBackToForm}
                variant="ghost"
                className="w-full text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                data-testid="urgencia-back-btn"
              >
                <ChevronLeft className="mr-1 h-4 w-4" />
                Volver al formulario y modificar mis síntomas
              </Button>
              <p className="text-xs text-slate-500 text-center mt-2">
                Si seleccionaste un síntoma por error, podés volver y corregirlo.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default UrgenciaPage;
